import ResourceCrudPage, {
  Field,
  TextInput,
  NumberInput,
  TextArea,
  StringListEditor,
  Tabs,
  Checkbox,
  Select,
  ColorInput,
  JsonEditor,
  isValidHex,
  isValidUrl,
} from "./ResourceCrudPage";

const IMAGE_KEYS = [
  "program_camps",
  "program_stem_coding",
  "program_tennis",
  "hero_main",
];

const ICON_TYPES = [
  { value: "", label: "— Default —" },
  { value: "sun", label: "Sun (camps)" },
  { value: "blocks", label: "Blocks (LEGO)" },
  { value: "robot", label: "Robot" },
  { value: "gears", label: "Gears (engineering)" },
  { value: "spark", label: "Spark" },
];

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const TABS = [
  { key: "basic", label: "Basic Info" },
  { key: "content", label: "Page Content" },
  { key: "visual", label: "Visual / Theme" },
  { key: "seo", label: "SEO" },
  { key: "advanced", label: "Advanced JSON" },
];

export default function AdminCampsPage() {
  return (
    <ResourceCrudPage
      resource="camps"
      title="Camps"
      subtitle="Public camp listings. Active camps appear on /camps and the sitemap. No booking or payment flows."
      testIdPrefix="camps"
      blank={() => ({
        slug: "",
        name: "",
        category: "Summer Camp",
        ages: "",
        age_range: "",
        dates: "",
        schedule: "",
        location: "Nexora Learning Centre, Richmond BC",
        description: "",
        short_description: "",
        full_description: "",
        highlights: [],
        sessions: [],
        price: "",
        image_key: "program_camps",
        image_url: "",
        logo_url: "",
        brand_label: "",
        accent_color: "",
        secondary_color: "",
        display_order: 100,
        active: true,
        seo_title: "",
        seo_description: "",
      })}
      validate={(f) => {
        if (!f.name?.trim()) return { ok: false, message: "Title is required." };
        if (!f.slug?.trim()) return { ok: false, message: "Slug is required." };
        if (!isValidHex(f.accent_color))
          return { ok: false, message: "Accent colour must be a valid hex (e.g. #FF8C42)." };
        if (!isValidHex(f.secondary_color))
          return { ok: false, message: "Secondary colour must be a valid hex (e.g. #FFF1E8)." };
        if (!isValidUrl(f.logo_url))
          return { ok: false, message: "Logo URL must start with http:// or https://." };
        if (!isValidUrl(f.image_url))
          return { ok: false, message: "Image URL must start with http:// or https://." };
        return { ok: true };
      }}
      renderRow={(item) => (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {item.accent_color && (
              <span
                className="h-3 w-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: item.accent_color }}
                aria-hidden="true"
              />
            )}
            <span className="pill bg-orange-50 text-[#C2410C]">
              {item.category || "Camp"}
            </span>
            <p className="font-display font-bold text-slate-900">{item.name}</p>
            {item.brand_label && (
              <span className="pill bg-amber-50 text-[#9A3412] text-[10px]">
                {item.brand_label}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">
            /camps/{item.slug} · {item.dates || "—"} · Ages {item.age_range || item.ages || "—"}
          </p>
        </div>
      )}
      renderForm={({ form, setField, errors, setError }) => (
        <Tabs items={TABS} testId="camps-tabs">
          {(activeTab) => (
            <>
              {/* ===== Basic Info ===== */}
              {activeTab === "basic" && (
                <div className="grid gap-5" data-testid="camps-tab-basic">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Camp title *">
                      <TextInput
                        value={form.name}
                        onChange={(v) => {
                          setField("name")(v);
                          if (!form.slug || form.slug === slugify(form.name)) {
                            setField("slug")(slugify(v));
                          }
                        }}
                        data-testid="camps-form-name"
                      />
                    </Field>
                    <Field label="Slug *" hint="/camps/{slug}">
                      <TextInput
                        value={form.slug}
                        onChange={setField("slug")}
                        data-testid="camps-form-slug"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Category" hint="e.g. Summer Camp, Spring Break">
                      <TextInput value={form.category} onChange={setField("category")} />
                    </Field>
                    <Field label="Age range" hint="e.g. Ages 6–10">
                      <TextInput value={form.age_range || form.ages} onChange={setField("age_range")} />
                    </Field>
                    <Field label="Display order" hint="Lower = first">
                      <NumberInput
                        value={form.display_order}
                        onChange={setField("display_order")}
                        data-testid="camps-form-display-order"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Dates" hint="e.g. Weekly, July–August">
                      <TextInput
                        value={form.dates}
                        onChange={setField("dates")}
                        data-testid="camps-form-dates"
                      />
                    </Field>
                    <Field label="Schedule" hint="e.g. Mon–Fri · 9:00 AM – 3:30 PM">
                      <TextInput
                        value={form.schedule}
                        onChange={setField("schedule")}
                        data-testid="camps-form-schedule"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Location">
                      <TextInput
                        value={form.location}
                        onChange={setField("location")}
                        data-testid="camps-form-location"
                      />
                    </Field>
                    <Field label="Price" hint="Display only — no checkout">
                      <TextInput
                        value={form.price}
                        onChange={setField("price")}
                        data-testid="camps-form-price"
                      />
                    </Field>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <Checkbox
                      value={form.active}
                      onChange={setField("active")}
                      label="Active (visible on public site)"
                      data-testid="camps-form-active"
                    />
                  </div>
                </div>
              )}

              {/* ===== Page Content ===== */}
              {activeTab === "content" && (
                <div className="grid gap-5" data-testid="camps-tab-content">
                  <Field label="Short description" hint="Card teaser. One sentence.">
                    <TextInput
                      value={form.short_description}
                      onChange={setField("short_description")}
                    />
                  </Field>
                  <Field label="Description" hint="Hero paragraph + overview on the camp detail page.">
                    <TextArea
                      rows={5}
                      value={form.description}
                      onChange={setField("description")}
                      data-testid="camps-form-description"
                    />
                  </Field>
                  <Field label="Full description" hint="Long-form detail used in the Overview section. Use blank lines to separate paragraphs.">
                    <TextArea
                      rows={6}
                      value={form.full_description}
                      onChange={setField("full_description")}
                    />
                  </Field>
                  <Field label="Highlights / bullets" hint="Shown in the hero and Every-day list.">
                    <StringListEditor
                      value={form.highlights}
                      onChange={setField("highlights")}
                      placeholder="Add a highlight"
                    />
                  </Field>
                  <Field label="Weekly themes / sessions" hint="Listed in the dark sidebar card.">
                    <StringListEditor
                      value={form.sessions}
                      onChange={setField("sessions")}
                      placeholder="e.g. Week 1: Motion & Machines"
                    />
                  </Field>
                </div>
              )}

              {/* ===== Visual / Theme ===== */}
              {activeTab === "visual" && (
                <div className="grid gap-5" data-testid="camps-tab-visual">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Accent colour"
                      hint="Primary hex used for hero CTA and accent strip."
                      error={errors?.accent_color}
                    >
                      <ColorInput
                        value={form.accent_color}
                        onChange={(v) => {
                          setField("accent_color")(v);
                          setError("accent_color", isValidHex(v) ? "" : "Must be #RGB or #RRGGBB");
                        }}
                        data-testid="camps-form-accent-color"
                      />
                    </Field>
                    <Field
                      label="Secondary colour"
                      hint="Soft background tint behind hero."
                      error={errors?.secondary_color}
                    >
                      <ColorInput
                        value={form.secondary_color}
                        onChange={(v) => {
                          setField("secondary_color")(v);
                          setError("secondary_color", isValidHex(v) ? "" : "Must be #RGB or #RRGGBB");
                        }}
                        data-testid="camps-form-secondary-color"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Logo URL"
                      hint="Direct image URL. Leave blank for icon fallback."
                      error={errors?.logo_url}
                    >
                      <TextInput
                        value={form.logo_url}
                        onChange={(v) => {
                          setField("logo_url")(v);
                          setError("logo_url", isValidUrl(v) ? "" : "Must start with http:// or https://");
                        }}
                        data-testid="camps-form-logo-url"
                      />
                    </Field>
                    <Field label="Icon type (fallback)" hint="Used when logo URL is empty.">
                      <Select
                        value={form.icon_type || ""}
                        onChange={setField("icon_type")}
                        options={ICON_TYPES}
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Hero image URL"
                      hint="Overrides the gradient placeholder when set."
                      error={errors?.image_url}
                    >
                      <TextInput
                        value={form.image_url}
                        onChange={(v) => {
                          setField("image_url")(v);
                          setError("image_url", isValidUrl(v) ? "" : "Must start with http:// or https://");
                        }}
                        data-testid="camps-form-image-url"
                      />
                    </Field>
                    <Field label="Image preset" hint="Legacy fallback if no image URL.">
                      <Select
                        value={form.image_key}
                        onChange={setField("image_key")}
                        options={IMAGE_KEYS}
                      />
                    </Field>
                  </div>
                  <Field label="Brand label" hint="Small tagline (e.g. 'Nexora Summer Camp').">
                    <TextInput
                      value={form.brand_label}
                      onChange={setField("brand_label")}
                      data-testid="camps-form-brand-label"
                    />
                  </Field>
                  {form.accent_color && form.secondary_color && (
                    <div className="rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid="camps-form-color-preview">
                      <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Preview</span>
                      <div
                        className="flex-1 rounded-xl p-3 text-sm font-semibold flex items-center justify-between"
                        style={{ backgroundColor: form.secondary_color, color: form.accent_color }}
                      >
                        <span>{form.name || "Camp name"}</span>
                        <span
                          className="rounded-full px-3 py-1 text-xs text-white"
                          style={{ backgroundColor: form.accent_color }}
                        >
                          CTA
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== SEO ===== */}
              {activeTab === "seo" && (
                <div className="grid gap-5" data-testid="camps-tab-seo">
                  <Field label="SEO title" hint="Used in <title>. Leave blank to use Camp title.">
                    <TextInput
                      value={form.seo_title}
                      onChange={setField("seo_title")}
                      data-testid="camps-form-seo-title"
                    />
                  </Field>
                  <Field label="Meta description" hint="155–160 chars recommended.">
                    <TextArea
                      rows={3}
                      value={form.seo_description}
                      onChange={setField("seo_description")}
                      data-testid="camps-form-seo-description"
                    />
                  </Field>
                </div>
              )}

              {/* ===== Advanced JSON ===== */}
              {activeTab === "advanced" && (
                <div className="grid gap-5" data-testid="camps-tab-advanced">
                  <Field
                    label="Sessions (JSON)"
                    hint="Optional structured sessions. Array of strings is also accepted via Page Content tab."
                    error={errors?.sessions_json}
                  >
                    <JsonEditor
                      value={form.sessions}
                      onChange={setField("sessions")}
                      onValidityChange={(ok, msg) => setError("sessions_json", ok ? "" : msg)}
                      rows={8}
                      testId="camps-form-sessions"
                    />
                  </Field>
                </div>
              )}
            </>
          )}
        </Tabs>
      )}
    />
  );
}
